import { useForm, useWatch } from "react-hook-form";

import type { ConstraintListItemDto, LanguageDto, UserRoleDocumentFolderPermissionsDto } from "@/api/types";
import { AudienceSelector } from "@/components/AudienceSelector/AudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormIconPicker } from "@/components/Form/FormIconPicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionSelector } from "@/components/PermissionSelector/PermissionSelector";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { getFlagIcon } from "@/helpers/flags";
import type { FormPermissions } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface DocumentRootFormValues {
  name: string;
  description: string;
  audience: ConstraintListItemDto[];
  permissions: FormPermissions<UserRoleDocumentFolderPermissionsDto>;
  iconChar: string;
}

export interface DocumentRootFormProps {
  isSubmitting: boolean;
  onSubmit: (data: DocumentRootFormValues) => void;
  defaultValues: DocumentRootFormValues;
  originalLanguageId?: LanguageDto["id"];
  isEditMode: boolean;
  folderId?: string;
}
export function DocumentRootForm(props: DocumentRootFormProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const sessionUser = useSessionUser();

  const formMethods = useForm<DocumentRootFormValues>({
    mode: "onChange",
    defaultValues: props.defaultValues,
  });
  const audience = useWatch({
    control: formMethods.control,
    name: "audience",
  });
  const permissions = useWatch({
    control: formMethods.control,
    name: "permissions",
  });

  const flagIcon = getFlagIcon({
    languageId: props.originalLanguageId ?? sessionUser.uiLanguage.id,
    width: 16,
  });

  const title = props.isEditMode
    ? t("page.document.root.create-or-edit.edit-mode.title")
    : t("page.document.root.create-or-edit.create-mode.title");

  const permissionFields = [
    { key: "canListDocuments", label: t("model.permission.document.can-list-document") },
    { key: "canCreateDocument", label: t("model.permission.document.can-create-document") },
    { key: "canDeleteDocument", label: t("model.permission.document.can-delete-document") },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.document.root.list.title"),
              to: routes.documents.rootList({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={props.onSubmit}>
        <Form.Container>
          <Form.Body>
            <Notice
              icon={<Translate01Icon />}
              variant="special"
              message={
                props.isEditMode
                  ? t("common.label.you-are-editing-in-original-content-language")
                  : t("common.label.your-input-will-be-auto-translated")
              }
            />
            <FormField label={t("model.document.root.name")} actions={flagIcon} required>
              <FormInput<DocumentRootFormValues>
                data-testid="documents-root-name"
                name="name"
                rules={{
                  required: {
                    message: t("components.form.error.required", {
                      inputName: t("model.document.root.name"),
                    }),
                    value: true,
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: NAME_MAX_LENGTH,
                    }),
                    value: NAME_MAX_LENGTH,
                  },
                }}
                placeholder={t("page.document.root.create-or-edit.create-mode.name.placeholder")}
              />
            </FormField>
            <FormField label={t("model.document.root.description")} actions={flagIcon}>
              <FormTextArea<DocumentRootFormValues>
                data-testid="documents-root-description"
                name="description"
                placeholder={t("page.document.root.create-or-edit.create-mode.description.placeholder")}
                rules={{
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: DESCRIPTION_MAX_LENGTH,
                    }),
                    value: DESCRIPTION_MAX_LENGTH,
                  },
                }}
              />
            </FormField>
            <FormField label={t("model.document.root.icon")} description={t("model.document.root.icon.description")}>
              <FormIconPicker<DocumentRootFormValues> name="iconChar" data-testid="documents-root-icon-char" />
            </FormField>
            <FormField
              label={t("common.entity.audience")}
              description={t("component.audience-section.description", {
                audienceTarget: t("common.entity.section").toLowerCase(),
              })}
            >
              <AudienceSelector
                defaultAudience={audience}
                isEditMode={props.isEditMode}
                onSave={(newAudience) => formMethods.setValue("audience", newAudience)}
              />
            </FormField>
            <FormField
              label={t("component.permission-section.title")}
              description={t("component.permission-section.description", {
                permissionTarget: t("common.entity.section").toLowerCase(),
              })}
            >
              <PermissionSelector
                defaultPermissions={permissions}
                fields={permissionFields}
                onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
              />
            </FormField>
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button data-testid="documents-root-submit" type="submit" isLoading={props.isSubmitting}>
                {props.isEditMode ? t("common.action.save") : t("common.action.create")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}
const NAME_MAX_LENGTH = 40;
const DESCRIPTION_MAX_LENGTH = 300;
